import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import "../styles/razorpay.css";
import emailjs from "@emailjs/browser";
import { WebsiteContext } from "../context/AppContext";
import { useNavigate, useParams } from "react-router-dom";
import { addDoc, collection, getDocs } from "firebase/firestore";
import { firestore } from "../firebase/firebase";
import HelmetComponent from "../components/common/HelmetComponent";
import Loader from "react-js-loader";
import {
  HiAcademicCap,
  HiUser,
  HiEnvelope,
  HiPhone,
  HiLockClosed,
  HiCheckCircle,
  HiClock,
} from "react-icons/hi2";
import { motion } from "framer-motion";

const RazorpayComponent = () => {
  const { id } = useParams();
  const [amount, setAmount] = useState(4000);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [course, setCourse] = useState("1-Month Spoken English Mastery Program");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const form = useRef();
  const navigate = useNavigate();
  const { color, dark } = useContext(WebsiteContext);

  const fetchBlogPosts = useCallback(async () => {
    try {
      const blogCollection = collection(firestore, "about_us");
      const blogSnapshot = await getDocs(blogCollection);
      const blogList = blogSnapshot.docs.map((doc) => ({
        ...doc.data(),
        _id: doc.id,
      }));

      const currentBlog = blogList.find((data) => data._id === id);

      if (currentBlog) {
        setCourse(currentBlog?.title || "1-Month Spoken English Mastery Program");
        if (currentBlog?.offer_price) {
          setAmount(currentBlog?.offer_price);
        }
      }
    } catch (err) {
      console.warn("Using default course parameters:", err);
    }
  }, [id]);

  useEffect(() => {
    fetchBlogPosts();
  }, [fetchBlogPosts]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !contact.trim()) {
      setError("Please fill in your name, email, and phone number.");
      return;
    }

    setError("");

    var options = {
      key: "rzp_live_MK17TqQ7xUUDjS",
      amount: amount * 100,
      currency: "INR",
      name: "FLUENT FABLE",
      description: course,
      handler: async function (response) {
        const student = {
          name,
          amount: parseInt(amount),
          purchased_course: course,
          email,
          purchased_date: new Date(),
          payment_id: response.razorpay_payment_id,
        };

        try {
          await addDoc(collection(firestore, "students"), student);
        } catch (err) {
          console.error("Error saving student to Firestore:", err);
        }

        emailjs
          .sendForm(
            "service_96kfsnh",
            "template_eza7hgm",
            form.current,
            "94QRcCXq7uSLVqVlb"
          )
          .then(
            () => {
              setLoading(true);
              setAlertMessage(
                "Payment successful! You will receive course joining details and WhatsApp group invite on your email shortly."
              );
              setTimeout(() => {
                setLoading(false);
                navigate("/about_us");
              }, 3000);
            },
            (err) => {
              console.error("EmailJS error:", err);
              setAlertMessage("Payment successful! Confirmation recorded.");
            }
          );
      },
      prefill: {
        name: name,
        email: email,
        contact: contact,
      },
      notes: {
        course: course,
      },
      theme: {
        color: "#7c3aed",
      },
    };

    if (window.Razorpay) {
      var pay = new window.Razorpay(options);
      pay.open();
    } else {
      setError("Payment gateway is loading. Please refresh and try again.");
    }
  };

  const perks = [
    "30 Days of Live Conversation Practice on Google Meet",
    "Monday to Friday (1 Hour Daily Interactive Coaching)",
    "Personalized Pronunciation, Grammar & Vocabulary Correction",
    "Exclusive WhatsApp Learner Discussion Group",
    "1-Year Post-Course Mentorship & Material Access",
  ];

  return (
    <>
      <HelmetComponent
        title="Secure Checkout | Fluent Fable"
        name="description"
        content="Enroll in Fluent Fable's 1-Month Spoken English Mastery Program with secure online payment."
      />

      <div className="checkout_page_wrapper">
        <div className="checkout_hero_header">
          <span className={`checkout_badge ${dark ? "checkout_badge_dark" : "checkout_badge_light"}`}>
            🔒 Secure 256-Bit SSL Checkout
          </span>
          <h1 className="checkout_main_title" style={{ color: color }}>
            Complete Your Enrollment
          </h1>
          <p className="checkout_main_subtitle" style={{ color: color }}>
            Secure your seat in our live spoken English mentorship cohort and start speaking with confidence.
          </p>
        </div>

        {!alertMessage ? (
          <div className="checkout_grid_layout">
            {/* Order Summary (Left) */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`order_summary_card ${dark ? "summary_card_dark" : "summary_card_light"}`}
            >
              <span className="summary_program_tag">
                <HiAcademicCap size={14} /> Live Training
              </span>

              <h2 className="summary_course_title" style={{ color: color }}>
                {course}
              </h2>

              <div className="summary_schedule_badge" style={{ color: color }}>
                <HiClock size={16} />
                <span>Mon–Fri | 1 Hr/Day Live on Google Meet</span>
              </div>

              <div className="summary_perks_list">
                {perks.map((perk, i) => (
                  <div key={i} className="summary_perk_item" style={{ color: color }}>
                    <HiCheckCircle size={17} className="summary_perk_icon" />
                    <span>{perk}</span>
                  </div>
                ))}
              </div>

              <div className="summary_price_box">
                <span className="summary_price_label" style={{ color: color }}>
                  Total Program Fee:
                </span>
                <span className="summary_price_amount">₹{amount}</span>
              </div>

              <div className="security_trust_badge" style={{ color: color }}>
                <HiLockClosed size={14} />
                <span>Powered by Razorpay Verified Payment Gateway</span>
              </div>
            </motion.div>

            {/* Student Details Form (Right) */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1 }}
              className={`checkout_form_card ${dark ? "form_card_dark" : "form_card_light"}`}
            >
              <h3 className="checkout_card_title" style={{ color: color }}>
                Student & Contact Details
              </h3>
              <p className="checkout_card_desc" style={{ color: color }}>
                Please enter accurate contact information so we can deliver your Google Meet access links and class schedules.
              </p>

              <form ref={form} onSubmit={handleSubmit}>
                {/* Name */}
                <div className="checkout_input_group">
                  <label className="checkout_label" style={{ color: color }}>
                    Full Name
                  </label>
                  <div className="checkout_input_wrap">
                    <HiUser className="checkout_icon" style={{ color: color }} />
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Manikandan"
                      className="checkout_input"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="checkout_input_group">
                  <label className="checkout_label" style={{ color: color }}>
                    Email Address
                  </label>
                  <div className="checkout_input_wrap">
                    <HiEnvelope className="checkout_icon" style={{ color: color }} />
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@example.com"
                      className="checkout_input"
                      required
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="checkout_input_group">
                  <label className="checkout_label" style={{ color: color }}>
                    WhatsApp Phone Number
                  </label>
                  <div className="checkout_input_wrap">
                    <HiPhone className="checkout_icon" style={{ color: color }} />
                    <input
                      type="tel"
                      name="contact"
                      value={contact}
                      onChange={(e) => setContact(e.target.value)}
                      placeholder="e.g. 9876543210"
                      className="checkout_input"
                      required
                    />
                  </div>
                </div>

                {/* Hidden form fields for EmailJS template */}
                <input type="hidden" name="course" value={course} />
                <input type="hidden" name="amount" value={amount} />

                {error && <div className="checkout_error_text">{error}</div>}

                {loading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "18px 0" }}>
                    <Loader type="spinner-cub" size={36} bgColor={color} color={color} />
                  </div>
                ) : (
                  <button type="submit" className="checkout_pay_btn">
                    <HiLockClosed size={16} />
                    <span>Proceed to Pay ₹{amount}</span>
                  </button>
                )}
              </form>
            </motion.div>
          </div>
        ) : (
          /* Confirmation Success Screen */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`checkout_success_screen ${dark ? "screen_dark" : "screen_light"}`}
          >
            <HiCheckCircle size={56} color="#10b981" style={{ marginBottom: 16 }} />
            <h2 style={{ fontSize: "1.6rem", fontWeight: 500, marginBottom: 12, color: color }}>
              Enrollment Confirmed!
            </h2>
            <p style={{ fontSize: "1rem", lineHeight: 1.6, opacity: 0.85, marginBottom: 24, color: color }}>
              {alertMessage}
            </p>
            <button
              className="checkout_pay_btn"
              style={{ maxWidth: 200, margin: "0 auto" }}
              onClick={() => navigate("/about_us")}
            >
              Back to About Us
            </button>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default RazorpayComponent;
